import { useCallback, useEffect } from "react";
import "./oscillator-page.css";
import {
  WaveType,
  FilterType,
  OscillatorSettings,
  defaultOscillatorSettings,
  initSynths,
  addSynth,
  removeSynth,
  updateSynth,
} from "../../../synth";
import { useLocalStorage } from "../../../hooks/use-local-storage";

const MAX_OSCILLATORS = 4;
const WAVE_TYPES: WaveType[] = ["sine", "square", "sawtooth", "triangle"];

function generateId(): string {
  return "osc-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function WaveIcon({ type }: { type: WaveType }) {
  return (
    <svg
      className="sk-wave-icon"
      viewBox="0 0 60 30"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {type === "sine" && (
        <path d="M0 15 C10 0, 20 0, 30 15 C40 30, 50 30, 60 15" fill="none" strokeWidth="2.5" strokeLinecap="round" />
      )}
      {type === "square" && (
        <path d="M0 15 L0 5 L30 5 L30 25 L60 25 L60 15" fill="none" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter" />
      )}
      {type === "sawtooth" && (
        <path d="M0 5 L30 25 L30 5 L60 25" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {type === "triangle" && (
        <path d="M0 15 L15 5 L45 25 L60 15" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function formatTime(value: number): string {
  if (value < 1) return `${Math.round(value * 1000)} ms`;
  return `${value.toFixed(2)} s`;
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDb(value: number): string {
  return `${value.toFixed(1)} dB`;
}

function formatFrequency(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(1)} kHz`;
  return `${Math.round(value)} Hz`;
}

function formatQ(value: number): string {
  return value.toFixed(1);
}

const FILTER_TYPES: FilterType[] = ["off", "lowpass", "highpass"];
const FILTER_LABELS: Record<FilterType, string> = { off: "OFF", lowpass: "LOW PASS", highpass: "HIGH PASS" };

interface SliderRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}

interface EnvelopeDisplayProps {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
}

function EnvelopeDisplay({ attack, decay, sustain, release }: EnvelopeDisplayProps) {
  const W = 300;
  const H = 80;
  const padX = 10;
  const padY = 8;

  // Power scaling so small values are still visible
  const sc = (t: number) => Math.pow(Math.max(t, 0.001), 0.4);
  const aS = sc(attack);
  const dS = sc(decay);
  const rS = sc(release);
  const totalADR = aS + dS + rS;
  const susS = totalADR * 0.45;
  const total = totalADR + susS;

  const drawW = W - padX * 2;
  const drawH = H - padY * 2;

  const x0 = padX;
  const x1 = padX + drawW * (aS / total);
  const x2 = x1 + drawW * (dS / total);
  const x3 = x2 + drawW * (susS / total);
  const x4 = padX + drawW;

  const yBot = H - padY;
  const yTop = padY;
  const yS = padY + (1 - sustain) * drawH;

  const line = `M${x0},${yBot} L${x1},${yTop} L${x2},${yS} L${x3},${yS} L${x4},${yBot}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      className="sk-envelope-display"
      preserveAspectRatio="none"
    >
      {/* Subtle amplitude grid lines */}
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={padX} y1={padY + f * drawH} x2={x4} y2={padY + f * drawH}
          stroke="rgba(31,3,24,0.07)" strokeWidth="1" />
      ))}
      {/* Baseline */}
      <line x1={x0} y1={yBot} x2={x4} y2={yBot} stroke="rgba(31,3,24,0.12)" strokeWidth="1" />
      {/* Phase labels */}
      <text x={(x0 + x1) / 2} y={H - 1} className="sk-env-label">A</text>
      <text x={(x1 + x2) / 2} y={H - 1} className="sk-env-label">D</text>
      <text x={(x2 + x3) / 2} y={H - 1} className="sk-env-label">S</text>
      <text x={(x3 + x4) / 2} y={H - 1} className="sk-env-label">R</text>
      {/* Filled area */}
      <path d={`${line} Z`} fill="rgba(224,108,159,0.18)" />
      {/* Envelope outline */}
      <path d={line} fill="none" stroke="var(--thulian-pink)" strokeWidth="2.5"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function SliderRow({ label, min, max, step, value, format, onChange }: SliderRowProps) {
  return (
    <div className="sk-slider-row">
      <label className="sk-slider-label">{label}</label>
      <input
        type="range"
        className="sk-slider"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      <span className="sk-slider-value">{format(value)}</span>
    </div>
  );
}

interface OscillatorCardProps {
  index: number;
  settings: OscillatorSettings;
  canRemove: boolean;
  onUpdate: (updates: Partial<Omit<OscillatorSettings, "id">>) => void;
  onRemove: () => void;
}

function OscillatorCard({ index, settings, canRemove, onUpdate, onRemove }: OscillatorCardProps) {
  const enabled = settings.enabled !== false;
  return (
    <div className={`sk-osc-card${enabled ? "" : " sk-osc-card--disabled"}`}>
      <div className="sk-osc-card-header">
        <span className="sk-osc-card-title pixel-font">OSC {index + 1}</span>
        <div className="sk-osc-card-header-actions">
          <button
            className={`sk-osc-enable-btn pixel-font${enabled ? " active" : ""}`}
            onClick={() => onUpdate({ enabled: !enabled })}
            aria-label={enabled ? `Disable oscillator ${index + 1}` : `Enable oscillator ${index + 1}`}
            aria-pressed={enabled}
          >
            {enabled ? "ON" : "OFF"}
          </button>
          {canRemove && (
            <button className="sk-osc-remove-btn" onClick={onRemove} aria-label={`Remove oscillator ${index + 1}`}>
              −
            </button>
          )}
        </div>
      </div>

      <div className="sk-osc-card-body">
        <div className="sk-wave-selector">
          {WAVE_TYPES.map((type) => (
            <button
              key={type}
              className={`sk-wave-btn${settings.waveType === type ? " active" : ""}`}
              onClick={() => onUpdate({ waveType: type })}
              aria-pressed={settings.waveType === type}
            >
              <WaveIcon type={type} />
              <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
            </button>
          ))}
        </div>

        <div className="sk-osc-sliders">
          <h4 className="sk-osc-sub-title pixel-font">ENVELOPE</h4>
          <EnvelopeDisplay
            attack={settings.attack}
            decay={settings.decay}
            sustain={settings.sustain}
            release={settings.release}
          />
          <SliderRow label="Attack"  min={0.001} max={2}   step={0.001} value={settings.attack}  format={formatTime}    onChange={(v) => onUpdate({ attack: v })} />
          <SliderRow label="Decay"   min={0.001} max={2}   step={0.001} value={settings.decay}   format={formatTime}    onChange={(v) => onUpdate({ decay: v })} />
          <SliderRow label="Sustain" min={0}     max={1}   step={0.01}  value={settings.sustain} format={formatPercent} onChange={(v) => onUpdate({ sustain: v })} />
          <SliderRow label="Release" min={0.001} max={5}   step={0.001} value={settings.release} format={formatTime}    onChange={(v) => onUpdate({ release: v })} />
        </div>

        <div className="sk-osc-sliders">
          <h4 className="sk-osc-sub-title pixel-font">VOLUME</h4>
          <SliderRow label="Volume" min={-30} max={0} step={0.5} value={settings.volume} format={formatDb} onChange={(v) => onUpdate({ volume: v })} />
        </div>

        <div className="sk-osc-sliders">
          <h4 className="sk-osc-sub-title pixel-font">FILTER</h4>
          <div className="sk-filter-selector">
            {FILTER_TYPES.map((type) => (
              <button
                key={type}
                className={`sk-filter-btn${(settings.filterType ?? "off") === type ? " active" : ""}`}
                onClick={() => onUpdate({ filterType: type })}
                aria-pressed={(settings.filterType ?? "off") === type}
              >
                {FILTER_LABELS[type]}
              </button>
            ))}
          </div>
          {(settings.filterType ?? "off") !== "off" && (
            <>
              <SliderRow label="Cutoff" min={80} max={18000} step={10} value={settings.filterFrequency ?? 2000} format={formatFrequency} onChange={(v) => onUpdate({ filterFrequency: v })} />
              <SliderRow label="Q" min={0.1} max={18} step={0.1} value={settings.filterQ ?? 1} format={formatQ} onChange={(v) => onUpdate({ filterQ: v })} />
            </>
          )}
        </div>

        <div className="sk-osc-transpose">
          <h4 className="sk-osc-sub-title pixel-font">TRANSPOSE</h4>
          <div className="sk-transpose-control">
            <button
              className="sk-transpose-btn"
              onClick={() => onUpdate({ transpose: Math.max(-24, (settings.transpose ?? 0) - 1) })}
              aria-label="Transpose down"
            >−</button>
            <span className="sk-transpose-value ubuntu-font">
              {(settings.transpose ?? 0) > 0 ? `+${settings.transpose}` : (settings.transpose ?? 0)} st
            </span>
            <button
              className="sk-transpose-btn"
              onClick={() => onUpdate({ transpose: Math.min(24, (settings.transpose ?? 0) + 1) })}
              aria-label="Transpose up"
            >+</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const DEFAULT_OSCILLATORS: OscillatorSettings[] = [{ ...defaultOscillatorSettings }];

export const OscillatorPage = () => {
  const [oscillators, setOscillators] = useLocalStorage<OscillatorSettings[]>(
    "synthkitty:oscillators",
    DEFAULT_OSCILLATORS,
  );

  // Sync all synths to saved state on mount
  useEffect(() => {
    initSynths(oscillators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-init synths when a project is loaded externally
  useEffect(() => {
    const handler = (e: Event) => {
      const { key, value } = (e as CustomEvent<{ key: string; value: string }>).detail;
      if (key !== 'synthkitty:oscillators') return;
      try { initSynths(JSON.parse(value) as OscillatorSettings[]); } catch { /* ignore */ }
    };
    window.addEventListener('synthkitty:storage-update', handler);
    return () => window.removeEventListener('synthkitty:storage-update', handler);
  }, []);

  const handleUpdate = useCallback(
    (id: string, updates: Partial<Omit<OscillatorSettings, "id">>) => {
      setOscillators((prev) => {
        const next = prev.map((o) => (o.id === id ? { ...o, ...updates } : o));
        const updated = next.find((o) => o.id === id);
        if (updated) updateSynth(updated);
        return next;
      });
    },
    [setOscillators],
  );

  const handleAdd = useCallback(() => {
    const newOsc: OscillatorSettings = {
      id: generateId(),
      waveType: "sine",
      attack: 0.01,
      decay: 0.1,
      sustain: 0.5,
      release: 0.5,
      volume: -6,
      transpose: 0,
      enabled: true,
      filterType: "off" as FilterType,
      filterFrequency: 2000,
      filterQ: 1,
    };
    addSynth(newOsc);
    setOscillators((prev) => [...prev, newOsc]);
  }, [setOscillators]);

  const handleRemove = useCallback(
    (id: string) => {
      removeSynth(id);
      setOscillators((prev) => prev.filter((o) => o.id !== id));
    },
    [setOscillators],
  );

  return (
    <div className="sk-oscillator-page">
      <div className="sk-osc-toolbar">
        <span className="pixel-font sk-osc-toolbar-label">
          {oscillators.length} OSC{oscillators.length !== 1 ? "S" : ""}
        </span>
        <button
          className="sk-add-osc-btn pixel-font"
          onClick={handleAdd}
          disabled={oscillators.length >= MAX_OSCILLATORS}
        >
          + ADD OSC
        </button>
      </div>

      <div className="sk-osc-cards">
        {oscillators.map((osc, index) => (
          <OscillatorCard
            key={osc.id}
            index={index}
            settings={osc}
            canRemove={oscillators.length > 1}
            onUpdate={(updates) => handleUpdate(osc.id, updates)}
            onRemove={() => handleRemove(osc.id)}
          />
        ))}
      </div>
    </div>
  );
};
